import { inject, Injectable } from "@angular/core";
import { NavigationItem } from "@claas/claas-ui-components/navigation/navigation";
import {
  catchError,
  concat,
  finalize,
  from,
  map,
  Observable,
  of,
  ReplaySubject,
  switchMap,
  take,
  tap,
  toArray,
} from "rxjs";
import {
  CoreMediaBreadcrumbItem,
  CoreMediaPageContext,
} from "./coremedia-navigation.model";
import { environment } from "../../../../../environments/environment";
import { API_HEADERS } from "../../../../shared/api-headers";
import { ApiClient } from "../../../../shared/data/api.client";
import { userContextStore } from "../../../../shared/data/user-context.store";
import {
  NavigationChannelConfig,
  resolveNavigationChannel,
} from "../../../../shared/util/navigation-channel-config";
import { computeSiteHash } from "../../../../shared/util/site-hash";

interface NavigationApiResponse {
  channelId: string;
  countryCode: string;
  userRole: string;
  root: NavigationNode;
}

interface NavigationApiEnvelope {
  data?: {
    root?: NavigationNode;
    content?: {
      page?: NavigationNode;
    };
  };
}

interface NavigationNode {
  id: string;
  segment?: string;
  fullPath?: string;
  targetType?: string;
  title: string;
  name?: string;
  hiddenInNavigation?: boolean | null;
  url?: string;
  openInNewTab?: boolean;
  pageIconId?: string | null;
  menuLinkTargetId?: string;
  menuLinkSiteName?: string;
  children?: NavigationNode[];
  context?: {
    children?: NavigationNode[];
  };
}

const DEFAULT_ICON_ID = "648466";
const MAX_VISIBLE_NAVIGATION_LEVELS = 2;

function resolveTitle(node: NavigationNode): string {
  return node.title?.trim() || node.name?.trim() || "";
}

function resolveIcon(
  pageIconId?: string | null,
): Pick<NavigationItem, "iconId" | "iconName" | "iconNamespace"> {
  const value =
    pageIconId ?? environment.navigation.defaultIconId ?? DEFAULT_ICON_ID;
  if (/^\d+$/.test(value)) {
    return { iconId: value, iconNamespace: "lib" };
  }
  return { iconName: value, iconNamespace: "lib" };
}
const NAVIGATION_PATH = `${environment.api.services.commons.coremediaV1}/navigation/retrieveMenuTree`;
const LOCALIZED_NAVIGATION_PATH = `${NAVIGATION_PATH}/localized`;

export const CMS_ROUTE_PREFIX = "/cms";
const LEGAL_ROUTE_PREFIX = "/legal";

@Injectable({ providedIn: "root" })
export class CoreMediaNavigationClient {
  private readonly api = inject(ApiClient);
  private readonly userContext = inject(userContextStore);
  private readonly pathToId = new Map<string, string>();
  private readonly pathToSiteName = new Map<string, string>();
  /**
   * Separate index for the footer legal-links tree (its own CoreMedia site, see
   * `loadLocalizedMenuTree`) — kept apart from `pathToId` so reloading one tree never evicts
   * path->id entries resolved from the other.
   */
  private readonly legalPathToId = new Map<string, string>();
  private readonly idToRoute = new Map<number, string>();
  private readonly pageContexts = new Map<string, CoreMediaPageContext>();
  private additionalTreesReady$ = new ReplaySubject<void>(1);
  private navigationGeneration = 0;

  loadNavigationItems(): Observable<NavigationItem[]> {
    this.navigationGeneration++;
    this.pageContexts.clear();
    this.additionalTreesReady$ = new ReplaySubject<void>(1);
    const channel = resolveNavigationChannel(
      this.userContext.country(),
      this.userContext.language(),
    );
    if (!channel) {
      console.error(
        `[CoreMediaNavigation] no channel configured for country='${this.userContext.country()}' language='${this.userContext.language()}'`,
      );
      return of([] as NavigationItem[]);
    }

    return from(computeSiteHash(channel.siteName)).pipe(
      switchMap((siteHash) =>
        this.api.get<NavigationApiResponse | NavigationApiEnvelope>(
          NAVIGATION_PATH,
          {
            params: { channelId: channel.channelId },
            headers: { [API_HEADERS.siteHash]: siteHash },
          },
        ),
      ),
      map((response) => {
        this.pathToId.clear();
        this.pathToSiteName.clear();
        this.idToRoute.clear();
        const rootChildren = this.extractRootChildren(
          response,
          this.pathToId,
          CMS_ROUTE_PREFIX,
        );
        const items = rootChildren
          .filter((node) => !node.hiddenInNavigation)
          .map((node) =>
            this.toNavigationItem(node, CMS_ROUTE_PREFIX, this.pathToId),
          );
        this.buildReverseIndex(this.pathToId, CMS_ROUTE_PREFIX);
        return items;
      }),
      tap((items) =>
        console.warn(
          `[CoreMediaNavigation] loaded ${items.length} top-level navigation item(s)`,
          items,
        ),
      ),
      catchError((error) => {
        console.error("[CoreMediaNavigation] failed to load navigation", error);
        return of([] as NavigationItem[]);
      }),
    );
  }

  /**
   * Loads a locale-resolved navigation tree from its own CoreMedia site — used for menus that
   * only exist as per-locale content (e.g. the footer legal-links menu), which lives outside the
   * current navigation channel and is therefore addressed explicitly by `channelId`/`siteName`
   * (see `ChannelLayoutClient.loadLocalizedLayout` for the equivalent channel-layout pattern).
   */
  loadLocalizedMenuTree(
    channelId: string,
    siteName: string,
  ): Observable<NavigationItem[]> {
    return from(computeSiteHash(siteName)).pipe(
      switchMap((siteHash) =>
        this.api.get<NavigationApiResponse | NavigationApiEnvelope>(
          LOCALIZED_NAVIGATION_PATH,
          {
            params: { channelId },
            headers: {
              [API_HEADERS.language]: this.userContext.language(),
              [API_HEADERS.fallbackLanguage]: environment.defaultLanguage,
              [API_HEADERS.siteHash]: siteHash,
            },
          },
        ),
      ),
      map((response) => {
        this.legalPathToId.clear();
        const rootChildren = this.extractRootChildren(
          response,
          this.legalPathToId,
        );
        return rootChildren
          .filter((node) => !node.hiddenInNavigation)
          .map((node) =>
            this.toNavigationItem(node, LEGAL_ROUTE_PREFIX, this.legalPathToId),
          );
      }),
      catchError((error) => {
        console.error(
          "[CoreMediaNavigation] failed to load localized menu tree",
          error,
        );
        return of([] as NavigationItem[]);
      }),
    );
  }

  loadAdditionalTrees(): Observable<void> {
    const ready$ = this.additionalTreesReady$;
    const generation = this.navigationGeneration;
    const trees: NavigationChannelConfig[] = [
      ...(environment.navigation.additionalTrees ?? []),
      ...(environment.navigation.globalTrees ?? []),
    ].filter((t) => t.channelId);

    if (trees.length === 0) {
      ready$.next();
      return of(undefined);
    }

    return concat(
      ...trees.map((tree) =>
        this.loadTree(
          tree.channelId,
          tree.siteName,
          tree.routePrefix ?? CMS_ROUTE_PREFIX,
          generation,
        ).pipe(map(() => undefined as void)),
      ),
    ).pipe(
      toArray(),
      map(() => undefined),
      finalize(() => ready$.next()),
    );
  }

  private loadTree(
    channelId: string,
    siteName: string,
    routePrefix: string,
    generation: number,
  ): Observable<void> {
    return from(computeSiteHash(siteName)).pipe(
      switchMap((siteHash) =>
        this.api.get<NavigationApiResponse | NavigationApiEnvelope>(
          NAVIGATION_PATH,
          {
            params: { channelId },
            headers: { [API_HEADERS.siteHash]: siteHash },
          },
        ),
      ),
      map((response) => {
        if (generation !== this.navigationGeneration) return;
        const tempIndex = new Map<string, string>();
        this.extractRootChildren(response, tempIndex, routePrefix, siteName);
        this.buildReverseIndex(tempIndex, routePrefix);
        for (const [path, id] of tempIndex) {
          this.pathToId.set(path, id);
          this.pathToSiteName.set(path, siteName);
        }
      }),
      catchError((error) => {
        console.error(
          `[CoreMediaNavigation] failed to load tree ${channelId}`,
          error,
        );
        return of(undefined);
      }),
    );
  }

  waitForAdditionalTrees(): Observable<void> {
    return this.additionalTreesReady$.pipe(take(1));
  }

  resolveRouteById(id: number): string | null {
    return this.idToRoute.get(id) ?? null;
  }

  resolveContentIdByPath(fullPath: string): Observable<string | null> {
    const normalized = this.normalizePath(fullPath);
    const existing = this.pathToId.get(normalized);
    if (existing) {
      return of(existing);
    }
    return this.waitForAdditionalTrees().pipe(
      map(() => this.pathToId.get(normalized) ?? null),
    );
  }

  resolveContentByPath(
    fullPath: string,
  ): Observable<{ id: string; siteName?: string } | null> {
    return this.resolveContentIdByPath(fullPath).pipe(
      map((id) => {
        if (!id) return null;
        const normalized = this.normalizePath(fullPath);
        const siteName = this.pathToSiteName.get(normalized);
        return { id, siteName };
      }),
    );
  }

  resolvePageByPath(
    fullPath: string,
    routePrefix = CMS_ROUTE_PREFIX,
  ): Observable<CoreMediaPageContext | null> {
    const route = this.toRoute(this.normalizePath(fullPath), routePrefix);
    const existing = this.pageContexts.get(route);
    if (existing) return of(existing);

    return this.waitForAdditionalTrees().pipe(
      map(() => this.pageContexts.get(route) ?? null),
    );
  }

  /**
   * Resolves a path within the footer legal-links tree (see `loadLocalizedMenuTree`) to its
   * CoreMedia content id — used by the `/legal/*` route, mirroring `resolveContentIdByPath` but
   * against the separate `legalPathToId` index.
   */
  resolveLegalContentIdByPath(fullPath: string): Observable<string | null> {
    return this.resolveIdByPath(fullPath, this.legalPathToId, () =>
      this.loadLocalizedMenuTree(
        environment.footerLegalLinks.channelId,
        environment.footerLegalLinks.siteName,
      ),
    );
  }

  private resolveIdByPath(
    fullPath: string,
    pathIndex: Map<string, string>,
    reload: () => Observable<unknown>,
  ): Observable<string | null> {
    const normalized = this.normalizePath(fullPath);
    const existing = pathIndex.get(normalized);
    if (existing) {
      return of(existing);
    }

    return reload().pipe(
      map(() => pathIndex.get(normalized) ?? null),
      catchError(() => of(null)),
    );
  }

  private toNavigationItem(
    node: NavigationNode,
    routePrefix: string,
    pathIndex: Map<string, string>,
    visibleLevel = 1,
  ): NavigationItem {
    const nestedChildren =
      visibleLevel < MAX_VISIBLE_NAVIGATION_LEVELS
        ? (node.context?.children ?? node.children ?? [])
            .filter((child) => !child.hiddenInNavigation)
            .map((child) =>
              this.toNavigationItem(
                child,
                routePrefix,
                pathIndex,
                visibleLevel + 1,
              ),
            )
        : [];

    const isExternalLink = node.targetType === "CMExternalLink";
    const contentPath = this.normalizePath(node.fullPath ?? `/${node.id}`);
    const route = isExternalLink
      ? undefined
      : this.toRoute(contentPath, routePrefix);
    const label = resolveTitle(node);
    const externalUrl = isExternalLink ? node.url : undefined;

    if (!isExternalLink) {
      pathIndex.set(contentPath, node.menuLinkTargetId ?? node.id);
      if (node.menuLinkSiteName) {
        this.pathToSiteName.set(contentPath, node.menuLinkSiteName);
      }
    }

    const externalAction = externalUrl
      ? () => {
          if (node.openInNewTab) {
            window.open(externalUrl, "_blank", "noopener,noreferrer");
          } else {
            window.location.href = externalUrl;
          }
        }
      : undefined;

    if (nestedChildren.length > 0) {
      const activeRoutes = route
        ? [
            route,
            ...nestedChildren
              .map((child) => child.route)
              .filter((value): value is string => !!value),
          ]
        : nestedChildren
            .map((child) => child.route)
            .filter((value): value is string => !!value);
      return {
        ...resolveIcon(node.pageIconId),
        label,
        route,
        action: externalAction,
        children: nestedChildren,
        activeRoutes,
      };
    }

    return {
      ...resolveIcon(node.pageIconId),
      label,
      route,
      action: externalAction,
      activeRoutes: route ? [route] : [],
    };
  }

  private extractRootChildren(
    response: NavigationApiResponse | NavigationApiEnvelope,
    pathIndex: Map<string, string>,
    routePrefix?: string,
    siteName?: string,
  ): NavigationNode[] {
    const root = this.extractRootNode(response);
    if (root?.children) {
      this.indexNode(root, pathIndex, routePrefix, siteName);
      return root.children ?? [];
    }
    return [];
  }

  private extractRootNode(
    response: NavigationApiResponse | NavigationApiEnvelope,
  ): NavigationNode | undefined {
    const rootFromDirectResponse = (response as NavigationApiResponse).root;
    if (rootFromDirectResponse) {
      return rootFromDirectResponse;
    }

    const rootFromEnvelope = (response as NavigationApiEnvelope).data?.root;
    if (rootFromEnvelope) {
      return rootFromEnvelope;
    }

    return (response as NavigationApiEnvelope).data?.content?.page;
  }

  private indexNode(
    node: NavigationNode,
    pathIndex: Map<string, string>,
    routePrefix?: string,
    siteName?: string,
    ancestors: readonly CoreMediaBreadcrumbItem[] | null = [],
    isRoot = true,
  ): void {
    const path = this.normalizePath(node.fullPath ?? `/${node.id}`);
    let breadcrumbs = ancestors;
    if (node.id) {
      pathIndex.set(path, node.menuLinkTargetId ?? node.id);
      if (node.menuLinkSiteName) {
        this.pathToSiteName.set(path, node.menuLinkSiteName);
      }
      if (routePrefix && node.targetType !== "CMExternalLink") {
        const route = this.toRoute(path, routePrefix);
        const title = resolveTitle(node);
        if (!isRoot) {
          if (!title) {
            console.warn(
              `[CoreMediaNavigation] missing page title for breadcrumb at '${route}'`,
            );
            breadcrumbs = null;
          } else if (ancestors) {
            breadcrumbs = [...ancestors, { label: title, route }];
          }
        }
        this.pageContexts.set(route, {
          id: node.menuLinkTargetId ?? node.id,
          siteName: node.menuLinkSiteName ?? siteName,
          breadcrumbs,
        });
      }
    }
    for (const child of node.children ?? node.context?.children ?? []) {
      this.indexNode(
        child,
        pathIndex,
        routePrefix,
        siteName,
        breadcrumbs,
        false,
      );
    }
  }

  private normalizePath(path: string): string {
    if (!path) return "/";
    const leadingSlash = path.startsWith("/") ? path : `/${path}`;
    return leadingSlash.replace(/\/{2,}/g, "/").replace(/\/+$/, "") || "/";
  }

  /**
   * Prefixes a content path with the given route segment (`/cms` or `/legal`, matching the
   * corresponding matcher in app.routes.ts), so links generated from the menu tree resolve to the
   * right content route.
   */
  private toRoute(contentPath: string, routePrefix: string): string {
    return contentPath === "/"
      ? routePrefix
      : this.normalizePath(`${routePrefix}${contentPath}`);
  }

  private buildReverseIndex(
    pathIndex: Map<string, string>,
    routePrefix: string,
  ): void {
    for (const [path, id] of pathIndex) {
      const numericId = parseInt(id, 10);
      if (!isNaN(numericId) && !this.idToRoute.has(numericId)) {
        this.idToRoute.set(numericId, this.toRoute(path, routePrefix));
      }
    }
  }
}
